import { createWebHistory, createRouter, RouteRecordRaw } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import { sessionReady } from "@/utils/sessionHandoff";

// Pages are lazy-loaded so each one ships as its own chunk and the first
// download only contains what the landing page needs.
//
// meta.requiresAuth — redirects to /login when there is no token.
// meta.guestOnly    — redirects to / when already logged in.
// meta.permissions / meta.staffCodes / meta.ownerOnly — see AccessRule in stores/auth.ts.
// The sidebar reads the same meta, so a menu item is shown iff its route is allowed.
const routes: RouteRecordRaw[] = [
  {
    path: "/",
    name: "EcommercePage",
    component: () => import("../pages/Dashboard/EcommercePage.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/account-settings",
    name: "AccountSettingsPage",
    component: () => import("../pages/Settings/AccountSettingsPage.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/settings-edit-password",
    name: "EditPasswordPage",
    component: () => import("../pages/Settings/EditPasswordPage.vue"),
    meta: { requiresAuth: true },
  },
  {
    path: "/black-list",
    name: "BlackListePage",
    component: () => import("../pages/Settings/BlackListePage.vue"),
    meta: { requiresAuth: true, permissions: ["black_list"], staffCodes: ["BLACK_LISTE"] },
  },

  // users — admin always, staff needs GESTION_UTILISATEURS
  {
    path: "/users-list",
    name: "UsersListPage",
    component: () => import("../pages/Users/UsersListPage.vue"),
    meta: { requiresAuth: true, staffCodes: ["GESTION_UTILISATEURS"] },
  },
  {
    path: "/add-user",
    name: "AddUserPage",
    component: () => import("../pages/Users/AddUserPage.vue"),
    meta: { requiresAuth: true, staffCodes: ["GESTION_UTILISATEURS"] },
  },
  {
    path: "/edit-user/:id",
    name: "EditUserPage",
    component: () => import("../pages/Users/EditUserPage.vue"),
    meta: { requiresAuth: true, staffCodes: ["GESTION_UTILISATEURS"] },
  },
  {
    path: "/history-user/:id",
    name: "HistoryUserPage",
    component: () => import("../pages/Users/HistoryUserPage.vue"),
    meta: { requiresAuth: true, staffCodes: ["GESTION_UTILISATEURS"] },
  },

  {
    path: "/phone-requests-list",
    name: "ListRequestPhonePage",
    component: () => import("@/pages/Customers/Demandes/ListRequestPhonePage.vue"),
    meta: { requiresAuth: true, ownerOnly: true },
  },
  {
    path: "/rib-requests-list",
    name: "ListRequestRibPage",
    component: () => import("@/pages/Customers/Demandes/ListRequestRibPage.vue"),
    meta: { requiresAuth: true, ownerOnly: true },
  },

  // claims
  {
    path: "/claims-list",
    name: "ClaimsListPage",
    component: () => import("@/pages/Claims/ClaimsListPage.vue"),
    meta: { requiresAuth: true, staffCodes: ["RECLAMATION"] },
  },
  {
    path: "/add-claims",
    name: "AddClaimPage",
    component: () => import("@/pages/Claims/AddClaimPage.vue"),
    meta: { requiresAuth: true, staffCodes: ["RECLAMATION"] },
  },
  {
    path: "/history-claim/:id",
    name: "HistoryClaimPage",
    component: () => import("@/pages/Claims/HistoryClaimPage.vue"),
    meta: { requiresAuth: true, staffCodes: ["RECLAMATION"] },
  },

  // pickup
  {
    path: "/pickup-list",
    name: "ListPickupPage",
    component: () => import("@/pages/PickUp/ListPickupPage.vue"),
    meta: { requiresAuth: true, staffCodes: ["RAMASSAGE"] },
  },
  {
    path: "/pickup-note-edit/:id/:refCode",
    name: "EditPickupPage",
    component: () => import("@/pages/PickUp/EditPickupPage.vue"),
    props: true,
    meta: { requiresAuth: true, staffCodes: ["RAMASSAGE"] },
  },
  {
    path: "/requests-pickup-list",
    name: "PickupRequestsPage",
    component: () => import("@/pages/PickUp/PickupRequestsPage.vue"),
    meta: { requiresAuth: true, staffCodes: ["RAMASSAGE"] },
  },

  // colis
  {
    path: "/colis-list",
    name: "ColisListePage",
    component: () => import("@/pages/Colis/ColisListePage.vue"),
    meta: { requiresAuth: true, permissions: ["Parcels"], staffCodes: ["Parcels"] },
  },
  {
    path: "/add-colis",
    name: "NewColisPage",
    component: () => import("@/pages/Colis/NewColisPage.vue"),
    meta: { requiresAuth: true, permissions: ["Parcels"], staffCodes: ["Parcels"] },
  },
  {
    path: "/edit-colis/:id/:code",
    name: "EditColisPage",
    component: () => import("@/pages/Colis/EditColisPage.vue"),
    meta: { requiresAuth: true, permissions: ["Parcels"], staffCodes: ["Parcels"] },
  },
  {
    path: "/new-colis-list",
    name: "NewColisListePage",
    component: () => import("@/pages/Colis/NewColisListePage.vue"),
    meta: { requiresAuth: true, permissions: ["Parcels"], staffCodes: ["Parcels"] },
  },
  {
    path: "/import-colis",
    name: "ImportColisPage",
    component: () => import("@/pages/Colis/ImportColisPage.vue"),
    meta: { requiresAuth: true, permissions: ["Parcels", "Import"], staffCodes: ["Parcels", "Import"] },
  },
  {
    path: "/import-colis-stock",
    name: "ImportColisStockPage",
    component: () => import("@/pages/Colis/ImportColisStockPage.vue"),
    meta: { requiresAuth: true, permissions: ["stock", "Import"], staffCodes: ["STOCK", "Import"] },
  },

  // stock
  {
    path: "/products-list",
    name: "ProductListPage",
    component: () => import("../pages/Stock/ProductListPage.vue"),
    meta: { requiresAuth: true, permissions: ["stock"], staffCodes: ["STOCK"] },
  },
  {
    path: "/add-product",
    name: "AddProductPage",
    component: () => import("../pages/Stock/AddProductPage.vue"),
    meta: { requiresAuth: true, permissions: ["stock"], staffCodes: ["STOCK"] },
  },
  {
    path: "/edit-product/:id",
    name: "EditProductPage",
    component: () => import("../pages/Stock/EditProductPage.vue"),
    meta: { requiresAuth: true, permissions: ["stock"], staffCodes: ["STOCK"] },
  },
  {
    path: "/colis-stock-list",
    name: "StockColisPage",
    component: () => import("../pages/Stock/StockColisPage.vue"),
    meta: { requiresAuth: true, permissions: ["stock", "Parcels"], staffCodes: ["STOCK"] },
  },
  {
    path: "/add-colis-stock",
    name: "AddColisStockPage",
    component: () => import("../pages/Stock/AddColisPage.vue"),
    meta: { requiresAuth: true, permissions: ["stock", "Parcels"], staffCodes: ["STOCK"] },
  },
  {
    path: "/edit-colis-stock/:id",
    name: "EditColisStockPage",
    component: () => import("@/pages/Stock/EditProdColisPage.vue"),
    meta: { requiresAuth: true, permissions: ["stock", "Parcels"], staffCodes: ["STOCK"] },
  },

  // delivery notes
  {
    path: "/delivery-notes",
    name: "InventoryNoteListePage",
    component: () => import("@/pages/Notes/Delivery/ListePage.vue"),
    meta: { requiresAuth: true, staffCodes: ["BON_LIVRAISON"] },
  },
  {
    path: "/add-delivery-note",
    name: "DeliveryAddNotePage",
    component: () => import("@/pages/Notes/Delivery/AddNotePage.vue"),
    meta: { requiresAuth: true, staffCodes: ["BON_LIVRAISON"] },
  },
  {
    path: "/edit-delivery-note/:id/:refNote",
    name: "DeliveryEditNotePage",
    component: () => import("@/pages/Notes/Delivery/EditNotePage.vue"),
    meta: { requiresAuth: true, staffCodes: ["BON_LIVRAISON"] },
  },
  {
    path: "/delivery-stock-notes",
    name: "InventoryStockNoteListePage",
    component: () => import("@/pages/Notes/StockDelivery/ListePage.vue"),
    meta: { requiresAuth: true, staffCodes: ["BON_LIVRAISON"] },
  },
  {
    path: "/add-delivery-stock-notes",
    name: "AddNoteBLStockPage",
    component: () => import("@/pages/Notes/StockDelivery/AddNotePage.vue"),
    meta: { requiresAuth: true, staffCodes: ["BON_LIVRAISON"] },
  },
  {
    path: "/edit-delivery-stock/:id/:refNote",
    name: "EditNoteBLStockPage",
    component: () => import("@/pages/Notes/StockDelivery/EditNotePage.vue"),
    meta: { requiresAuth: true, staffCodes: ["BON_LIVRAISON"] },
  },

  {
    path: "/logout",
    name: "LogoutPage",
    component: () => import("../pages/LogoutPage.vue"),
    meta: { requiresAuth: true },
  },

  // return notes
  {
    path: "/return-client-liste",
    name: "ReturnListePage",
    component: () => import("@/pages/Notes/Return/Client/ReturnListePage.vue"),
    meta: { requiresAuth: true, staffCodes: ["BON_LIVRAISON"] },
  },

  // invoices
  {
    path: "/invoices-liste",
    name: "InvoiceListPage",
    component: () => import("@/pages/Invoices/InvoiceListPage.vue"),
    meta: { requiresAuth: true, staffCodes: ["FACTURES"] },
  },

  // virements
  {
    path: "/virements-liste",
    name: "VirementListPage",
    component: () => import("@/pages/Virements/VirementListPage.vue"),
    meta: { requiresAuth: true, ownerOnly: true },
  },

  // settings
  {
    path: "/profile",
    name: "ProfilePage",
    component: () => import("../pages/Profile/Profile.vue"),
    meta: { requiresAuth: true },
  },

  {
    path: "/login",
    name: "LoginPage",
    component: () => import("../pages/Authentication/LoginPage.vue"),
    meta: { guestOnly: true },
  },
  {
    path: "/register",
    name: "RegisterPage",
    component: () => import("../pages/Authentication/RegisterPage.vue"),
    meta: { guestOnly: true },
  },
  {
    path: "/complete-register/:id/:token",
    name: "CompleteRegisterPage",
    component: () => import("../pages/Authentication/CompleteRegisterPage.vue"),
  },
  {
    path: "/documentation-api",
    name: "ApiPage",
    component: () => import("@/pages/Settings/DocAPI/ApiPage.vue"),
    meta: { requiresAuth: true, ownerOnly: true },
  },
  {
    path: "/documents",
    name: "VerificationPage",
    component: () => import("@/pages/Settings/verificationDocs/VerificationPage.vue"),
    meta: { requiresAuth: true, ownerOnly: true },
  },
  {
    path: "/shopify",
    name: "ShopifyPage",
    component: () => import("@/pages/Settings/Stores/AddShopifyPage.vue"),
    meta: { requiresAuth: true, ownerOnly: true },
  },
  {
    path: "/sheets",
    name: "SheetsPage",
    component: () => import("@/pages/Settings/Stores/SheetsPage.vue"),
    meta: { requiresAuth: true, ownerOnly: true },
  },
  {
    path: "/ColisConfirmed",
    name: "ColisConfirmedPage",
    component: () => import("@/pages/Settings/Stores/ColisConfirmedPage.vue"),
    meta: { requiresAuth: true, ownerOnly: true },
  },
  {
    path: "/ColisErrones",
    name: "ColisErronesPage",
    component: () => import("@/pages/Settings/Stores/ColisErronesPage.vue"),
    meta: { requiresAuth: true, ownerOnly: true },
  },
  {
    path: "/webhooks",
    name: "WebhookPage",
    component: () => import("@/pages/Settings/DocAPI/WebhookPage.vue"),
    meta: { requiresAuth: true, ownerOnly: true },
  },

  { path: "/:pathMatch(.*)*", name: "ErrorPage", component: () => import("../pages/ErrorPage.vue") },
];

const router = createRouter({
  history: createWebHistory(),
  linkExactActiveClass: "active",
  routes: routes,
  scrollBehavior() {
    return { top: 0, behavior: "smooth" };
  },
});

// Refresh permissions on navigation, throttled, so revoked rights disappear
// without hammering the API (this runs for every open tab of every user).
const PERMS_REFRESH_MS = 60_000;
let lastPermsFetch = 0;

export function refreshPermissionsThrottled() {
  const auth = useAuthStore();
  const now = Date.now();
  if (auth.isAuthenticated && now - lastPermsFetch > PERMS_REFRESH_MS) {
    lastPermsFetch = now;
    auth.fetchPermissions();
  }
}

// On the first navigation of a page load, wait for fresh permissions instead of
// trusting the persisted copy (it may be stale, or empty right after a staff-link login).
let firstNavigation = true;

router.beforeEach(async (to) => {
  const auth = useAuthStore();
  if (firstNavigation) await sessionReady();

  if (to.meta.requiresAuth && !auth.isAuthenticated) {
    auth.clearAuth();
    return { name: "LoginPage" };
  }

  if (to.meta.guestOnly && auth.isAuthenticated) {
    return { name: "EcommercePage" };
  }

  if (firstNavigation && auth.isAuthenticated) {
    firstNavigation = false;
    lastPermsFetch = Date.now();
    await auth.fetchPermissions();
  }

  if (!auth.canAccess(to.meta)) {
    return { name: "ErrorPage", params: { pathMatch: to.path.substring(1).split("/") } };
  }
});

router.afterEach(refreshPermissionsThrottled);

export default router;
